/* Suchvorschläge: lädt die Section predictive-search über die Section Rendering API. Optional Tipp-Effekt im Platzhalter (data-terms). */

const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

class KgPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.predictive-search__panel');
    this.live = this.querySelector('.predictive-search__live');
    this.status = this.querySelector('[data-search-status]');
    this.defaults = this.querySelector('.predictive-search__default');
    this.resetButton = this.querySelector('.search-form__reset');
    this.sectionId = this.dataset.section || 'predictive-search';
    this.cache = new Map();
    if (!this.form || !this.input || !this.panel) return;

    /*
      Das Suchfeld sitzt im Header, der oben klebt. Zusammen mit scroll-padding-top
      versucht der Browser beim Fokus, das Feld unter den Header zu schieben, und
      scrollt die Seite nach oben. Waehrend des Fokus wird der Abstand deshalb kurz
      auf null gesetzt.
    */
    this.haltePosition = () => {
      const root = document.documentElement;
      const vorher = root.style.scrollPaddingTop;
      root.style.scrollPaddingTop = '0px';
      window.setTimeout(() => {
        root.style.scrollPaddingTop = vorher;
      }, 400);
    };
    this.input.addEventListener('pointerdown', this.haltePosition);
    this.input.addEventListener('focus', this.haltePosition);

    this.input.addEventListener('input', debounce(() => this.onInput(), 200));
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2 && this.live.innerHTML.trim()) this.open();
      else this.showDefaults();
    });
    this.form.addEventListener('reset', () => {
      this.live.innerHTML = '';
      this.resetButton.hidden = true;
      this.input.focus();
      this.showDefaults();
    });
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.onDocumentClick = (event) => {
      if (this.contains(event.target) || event.target.closest('[data-dialog-open]')) return;
      /*
        Im Schuber nicht schliessen: Dort ist die Liste der ganze Inhalt, nicht ein Aufklapper
        ueber der Seite. Ein Tipp daneben — auf den Kopf, auf eine freie Stelle — liess sonst
        die beliebten Suchbegriffe und Produkte verschwinden, ohne dass man etwas eingegeben
        hatte. Am Rechner bleibt es beim Schliessen, dort haengt die Liste wirklich an der Seite.
      */
      if (this.closest('dialog[open]')) return;
      this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
    this.resetButton.hidden = this.input.value.trim().length === 0;
    this.setupTypewriter();

    const dialog = this.closest('kg-dialog');
    if (dialog) {
      dialog.addEventListener('dialog:open', () => {
        /*
          Jedes Oeffnen faengt leer an. Ein stehengebliebener Begriff von vorhin beantwortet
          eine Frage, die gerade niemand gestellt hat — und man muesste ihn erst loeschen.
        */
        this.input.value = '';
        this.resetButton.hidden = true;
        this.live.innerHTML = '';
        this.showDefaults();
      });
      dialog.addEventListener('dialog:close', () => this.close());
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.controller?.abort();
    this.stopTypewriter();
  }

  setupTypewriter() {
    const terms = (this.dataset.terms || '').split('|').map((term) => term.trim()).filter(Boolean);
    if (!terms.length) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.terms = terms;
    this.termsPrefix = this.dataset.termsPrefix ? `${this.dataset.termsPrefix} ` : '';
    this.basePlaceholder = this.input.placeholder;
    this.input.addEventListener('focus', () => this.stopTypewriter());
    this.input.addEventListener('blur', () => this.scheduleTypewriter(1500));
    this.onVisibility = () => (document.hidden ? this.stopTypewriter() : this.scheduleTypewriter(1500));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.scheduleTypewriter(3000);
  }

  scheduleTypewriter(delay) {
    this.stopTypewriter();
    if (document.activeElement === this.input || this.input.value) return;
    this.typeTimer = setTimeout(() => {
      this.swapPlaceholder(this.termsPrefix.trim(), () => this.typeTerm(0));
    }, delay);
  }

  stopTypewriter() {
    clearTimeout(this.typeTimer);
    clearTimeout(this.swapTimer);
    if (this.basePlaceholder === undefined || this.input.placeholder === this.basePlaceholder) return;
    this.swapPlaceholder(this.basePlaceholder);
  }

  swapPlaceholder(text, done) {
    clearTimeout(this.swapTimer);
    this.classList.add('is-swapping');
    this.swapTimer = setTimeout(() => {
      this.input.placeholder = text;
      this.classList.remove('is-swapping');
      if (done) this.swapTimer = setTimeout(done, 300);
    }, 260);
  }

  typeTerm(index) {
    const term = this.terms[index % this.terms.length];
    let length = 0;
    const step = () => {
      if (document.activeElement === this.input || this.input.value) {
        this.stopTypewriter();
        return;
      }
      length += 1;
      this.input.placeholder = this.termsPrefix + term.slice(0, length);
      if (length < term.length) {
        this.typeTimer = setTimeout(step, 70);
      } else {
        this.typeTimer = setTimeout(() => this.eraseTerm(index, term), 1800);
      }
    };
    step();
  }

  eraseTerm(index, term) {
    let length = term.length;
    const step = () => {
      if (document.activeElement === this.input || this.input.value) {
        this.stopTypewriter();
        return;
      }
      length -= 1;
      this.input.placeholder = this.termsPrefix + term.slice(0, length);
      if (length > 0) {
        this.typeTimer = setTimeout(step, 35);
      } else {
        this.typeTimer = setTimeout(() => this.typeTerm(index + 1), 400);
      }
    };
    step();
  }

  async onInput() {
    const query = this.input.value.trim();
    this.resetButton.hidden = query.length === 0;
    if (query.length < 2) {
      this.showDefaults();
      return;
    }
    this.setAttribute('loading', '');
    try {
      const html = await this.fetchResults(query);
      if (this.input.value.trim() !== query) return;
      this.live.innerHTML = html;
      if (this.defaults) this.defaults.hidden = true;
      this.open();
      this.melde(this.live.querySelectorAll('a[href]').length);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.removeAttribute('loading');
    }
  }

  async fetchResults(query) {
    if (this.cache.has(query)) return this.cache.get(query);
    this.controller?.abort();
    this.controller = new AbortController();
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: query,
      'resources[type]': this.dataset.resources || 'product,query,collection,page',
      'resources[limit]': this.dataset.resources ? '8' : '6',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      section_id: this.sectionId,
    });
    const response = await fetch(`${root}search/suggest?${params}`, { signal: this.controller.signal });
    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const html = doc.querySelector(`#shopify-section-${this.sectionId}`)?.innerHTML || '';
    this.cache.set(query, html);
    return html;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (this.panel.hidden) return;
    const links = [...this.panel.querySelectorAll('a')];
    if (!links.length) return;
    event.preventDefault();
    const current = links.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') next = current + 1 >= links.length ? 0 : current + 1;
    else next = current <= 0 ? links.length - 1 : current - 1;
    links[next].focus();
  }

  showDefaults() {
    this.live.innerHTML = '';
    if (!this.defaults) {
      this.close();
      return;
    }
    this.defaults.hidden = false;
    this.open();
  }

  open() {
    this.panel.hidden = false;
    this.classList.add('is-open');
  }

  close() {
    this.panel.hidden = true;
    this.classList.remove('is-open');
    this.melde(null);
  }

  /*
    Was sichtbar aufklappt, muss auch gesagt werden — und zwar hier, nicht ueber
    `aria-expanded` am Eingabefeld: Dort darf es nicht stehen, und es meldet ohnehin nur „zu"
    und „auf", nicht, ob etwas gefunden wurde.

    `null` leert die Zeile. Ohne das Leeren bliebe der letzte Stand stehen und wuerde beim
    naechsten Mal mit gleicher Zahl nicht noch einmal vorgelesen.
  */
  melde(anzahl) {
    if (!this.status) return;
    if (anzahl === null) {
      this.status.textContent = '';
      return;
    }
    const vorlage = anzahl === 1 ? this.status.dataset.statusOne : this.status.dataset.statusOther;
    this.status.textContent = (vorlage || '').replace('[anzahl]', anzahl);
  }
}

customElements.define('kg-predictive-search', KgPredictiveSearch);
